import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import ThemeToggle from './ThemeToggle';
import caLogo from '../images/logo.png';

// Icons
import { Database, Activity, ArrowLeft, Book } from 'lucide-react';

// Sections
import ConnectionsSection from './sections/ConnectionsSection';
import MigrationsSection from './sections/MigrationsSection';
import MonitorSection from './sections/MonitorSection';

const MigrationIcon = ({ size = 20, color = 'currentColor' }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="1.75" 
        strokeLinecap="round" 
        strokeLinejoin="round"
    >
        <ellipse cx="7" cy="6" rx="4" ry="2" />
        <path d="M3 6v4c0 1.1 1.8 2 4 2s4-.9 4-2V6" />
        <path d="M3 10v4c0 1.1 1.8 2 4 2s4-.9 4-2v-4" />
        <path d="M12 12h8" />
        <path d="M17 9l3 3-3 3" />
        <ellipse cx="17" cy="18" rx="4" ry="2" />
        <path d="M13 18v3c0 1.1 1.8 2 4 2s4-.9 4-2v-3" />
    </svg>
);

const ConnectionIcon = ({ size = 20, color = 'currentColor' }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="1.75" 
        strokeLinecap="round" 
        strokeLinejoin="round"
    >
        <ellipse cx="6" cy="7" rx="3.5" ry="1.8" />
        <path d="M2.5 7v4c0 1 1.57 1.8 3.5 1.8s3.5-.8 3.5-1.8V7" />
        <path d="M2.5 11v4c0 1 1.57 1.8 3.5 1.8s3.5-.8 3.5-1.8v-4" />
        <ellipse cx="18" cy="7" rx="3.5" ry="1.8" />
        <path d="M14.5 7v4c0 1 1.57 1.8 3.5 1.8s3.5-.8 3.5-1.8V7" />
        <path d="M14.5 11v4c0 1 1.57 1.8 3.5 1.8s3.5-.8 3.5-1.8v-4" />
        <path d="M6 16.8v1.7c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2v-1.7" />
        <path d="M12 18.5v3" />
        <circle cx="12" cy="21.5" r="1" fill={color} />
    </svg>
);


function Dashboard({
  apiUrl,
  onBackToHome,
  activeSection,
  setActiveSection,
  connections,
  jobs,
  loading,
  setLoading,
  showNotification,
  onRefreshConnections,
  onRefreshJobs,
  theme,
  toggleTheme
}) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    onRefreshConnections();
    onRefreshJobs();
    const interval = setInterval(() => {
      onRefreshConnections();
      onRefreshJobs();
    }, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [onRefreshConnections, onRefreshJobs]);

  const sections = [
    { id: 'connections', label: 'Connections', icon: <ConnectionIcon size={20} /> },
    { id: 'migrations', label: 'Migrations', icon: <MigrationIcon size={20} /> },
    { id: 'monitor', label: 'Monitor', icon: <Activity size={20} /> }
  ];

  const renderSection = () => {
    const commonProps = {
      apiUrl,
      connections,
      jobs,
      onRefresh: () => {
        onRefreshConnections();
        onRefreshJobs();
      },
      loading,
      setLoading,
      showNotification
    };

    switch (activeSection) {
      case 'connections':
        return <ConnectionsSection {...commonProps} />;
      case 'migrations':
        return <MigrationsSection {...commonProps} />;
      case 'monitor':
        return <MonitorSection {...commonProps} />;
      default:
        return <ConnectionsSection {...commonProps} />;
    }
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <button onClick={onBackToHome} title="Back" style={{ background: 'transparent', border: 'none', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px 0 0', opacity: 0.7, transition: 'opacity 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.opacity = '1'} onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}>
            <ArrowLeft size={20} />
          </button>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            ☰
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={caLogo} alt="Cloud Ambassadors" style={{ height: '28px', objectFit: 'contain' }} />
            <h1>Data Lift</h1>
          </div>
        </div>
        <div className="header-right">
          <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
          <button className="btn-secondary" onClick={() => window.open('https://docs.dmsplatform.io', '_blank')} title="View Documentation">
            <Book size={16} />
            Documentation
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="dashboard-body">
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <nav className="sidebar-nav">
            {sections.map(section => (
              <div
                key={section.id}
                className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
                title={sidebarCollapsed ? section.label : ""}
              >
                <div className={`nav-icon ${section.id}-icon`}>{section.icon}</div>
                <div className="nav-label">{section.label}</div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="dashboard-main">
          {/* Active Section Content */}
          {renderSection()}
        </main>
      </div>


    </div>
  );
}

export default Dashboard;